/* src/components/admin/UpdateZipcodeDispatchZoneModal.tsx */
import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { adminZipcodeDispatchZoneService, AdminZipcodeDispatchZone, UpdateZipcodeDispatchZoneData } from "../../services/admin.zipcode-dispatch-zone.service";
import { countryService, CountryFull } from "../../services/country.service";
import { errorService } from "../../services/error.service";
import { utilsService } from "../../services/utils.service";
import useForm, { ValidationRules } from "../../hooks/useForm";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faSave,
    faGlobe,
    faMapPin,
    faToggleOn
} from "@fortawesome/free-solid-svg-icons";
import styles from "../../styles/components/UpdateZipcodeDispatchZoneModal.module.css";
import modalStyles from "../../styles/common/Modal.module.css";

interface UpdateZipcodeDispatchZoneModalProps {
    show: boolean;
    onHide: () => void;
    onSuccess: () => void;
    zone: AdminZipcodeDispatchZone | null;
}

interface UpdateZipcodeDispatchZoneFormState {
    iso2Country: string;
    zipcode: string;
    status: number;
}

const UpdateZipcodeDispatchZoneModal: React.FC<UpdateZipcodeDispatchZoneModalProps> = ({
    show,
    onHide,
    onSuccess,
    zone,
}) => {
    const [countries, setCountries] = useState<CountryFull[]>([]);
    const [loadingCountries, setLoadingCountries] = useState(false);
    const [loading, setLoading] = useState(false);
    const [locationInfo, setLocationInfo] = useState<{ city?: string; state?: string } | null>(null);
    const [zipcodeError, setZipcodeError] = useState<string | null>(null);
    const [validatingZipcode, setValidatingZipcode] = useState(false);

    const initialValues = React.useMemo<UpdateZipcodeDispatchZoneFormState>(() => {
        if (!zone) {
            return {
                iso2Country: "",
                zipcode: "",
                status: 1,
            };
        }
        return {
            iso2Country: zone.iso2Country || zone.isO2Country || "",
            zipcode: zone.zipcode || "",
            status: zone.status,
        };
    }, [zone]);

    const validationRules = React.useMemo<ValidationRules<UpdateZipcodeDispatchZoneFormState>>(() => ({
        iso2Country: (val) => (!val ? "El país es obligatorio" : null),
        zipcode: (val) => (!val?.trim() ? "El código postal es obligatorio" : null),
    }), []);

    const {
        values,
        errors,
        handleChange,
        handleSubmit,
        resetForm,
        setValues,
        setErrors,
    } = useForm<UpdateZipcodeDispatchZoneFormState>(
        initialValues,
        validationRules
    );

    const validateZipcode = async (zip: string, country: string) => {
        if (!zip.trim() || !country) {
            setLocationInfo(null);
            setZipcodeError(null);
            return false;
        }
        try {
            setValidatingZipcode(true);
            setZipcodeError(null);
            const loc = await utilsService.getLocationByZipcode({
                iso2code: country.toLowerCase(),
                zipcode: zip.trim()
            });
            if (loc) {
                setLocationInfo({ city: loc.city, state: loc.state });
                setZipcodeError(null);
                return true;
            } else {
                setLocationInfo(null);
                setZipcodeError(`Código postal inválido para ${country.toUpperCase()}.`);
                return false;
            }
        } catch (err) {
            console.error("Error validating zipcode:", err);
            setLocationInfo(null);
            setZipcodeError("Error al validar código postal.");
            return false;
        } finally {
            setValidatingZipcode(false);
        }
    };

    // Fetch countries list on mount
    useEffect(() => {
        const fetchCountries = async () => {
            try {
                setLoadingCountries(true);
                const data = await countryService.getAllCountries();
                const senders = data.filter((c) => c.send === true);
                setCountries(senders.length > 0 ? senders : data);
            } catch (error) {
                console.error("Error loading countries:", error);
            } finally {
                setLoadingCountries(false);
            }
        };
        fetchCountries();
    }, []);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (values.zipcode && values.iso2Country) {
                validateZipcode(values.zipcode, values.iso2Country);
            } else {
                setLocationInfo(null);
                setZipcodeError(null);
            }
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [values.zipcode, values.iso2Country]);

    useEffect(() => {
        if (show && zone) {
            setValues({
                iso2Country: zone.iso2Country || zone.isO2Country || "",
                zipcode: zone.zipcode || "",
                status: zone.status,
            });
            setErrors({});
            setLocationInfo(null);
            setZipcodeError(null);
        } else if (!show) {
            resetForm();
            setLocationInfo(null);
            setZipcodeError(null);
        }
    }, [show, zone, setValues, setErrors, resetForm]);

    const onSubmit = async () => {
        try {
            setLoading(true);

            // Double check validation before submit
            const isValid = await validateZipcode(values.zipcode, values.iso2Country);
            if (!isValid) {
                setLoading(false);
                return;
            }

            const payload: UpdateZipcodeDispatchZoneData = {
                id: zone!.id,
                iso2Country: values.iso2Country.toUpperCase(),
                zipcode: values.zipcode.trim(),
                status: Number(values.status),
            };

            const response = await adminZipcodeDispatchZoneService.updateZipcodeDispatchZone(payload);

            if (response.statusCode === 200) {
                onHide();
                onSuccess();
                await Swal.fire({
                    icon: "success",
                    title: "¡Éxito!",
                    text: "La zona de despacho por código postal ha sido actualizada correctamente.",
                    confirmButtonText: "Aceptar",
                    confirmButtonColor: "var(--color-primary)",
                });
            } else {
                errorService.showErrorAlert(response, "Error al Actualizar Zona de Despacho");
            }
        } catch (error: any) {
            console.error("Error in UpdateZipcodeDispatchZoneModal:", error);
            errorService.showErrorAlert(error, "Error de Conexión");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
            size="lg"
            className={styles.updateZipcodeModal}
            contentClassName={styles.scaleIn}
            animation={false}
        >
            <Modal.Header closeButton>
                <Modal.Title as="h3" className={modalStyles.modalTitle}>
                    Editar Zona de Despacho
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <p className={styles.modalSubtitle}>
                    Modifica la información de la zona de despacho por código postal.
                </p>
                <Form onSubmit={handleSubmit(onSubmit)} id="update-zipcode-form" noValidate>
                    <div className={styles.formGrid}>
                        <Form.Group>
                            <Form.Label>País</Form.Label>
                            <div className={styles.inputWrapper}>
                                <FontAwesomeIcon icon={faGlobe} className={styles.inputIcon} />
                                <Form.Select
                                    name="iso2Country"
                                    value={values.iso2Country}
                                    onChange={handleChange}
                                    className={`${styles.updateZipcodeModalInput} ${errors.iso2Country ? styles.inputError : ""}`}
                                    disabled={loading || loadingCountries}
                                >
                                    {countries.map((c) => (
                                        <option key={c.iso2Country} value={c.iso2Country}>
                                            {c.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </div>
                            {errors.iso2Country && <div className={styles.errorText}>{errors.iso2Country}</div>}
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>Código Postal</Form.Label>
                            <div className={styles.inputWrapper}>
                                <FontAwesomeIcon icon={faMapPin} className={styles.inputIcon} />
                                <Form.Control
                                    type="text"
                                    name="zipcode"
                                    placeholder="Código Postal (ej. 1010)"
                                    value={values.zipcode}
                                    onChange={handleChange}
                                    className={`${styles.updateZipcodeModalInput} ${errors.zipcode || zipcodeError ? styles.inputError : ""}`}
                                    disabled={loading}
                                    maxLength={20}
                                />
                            </div>
                            {errors.zipcode && <div className={styles.errorText}>{errors.zipcode}</div>}
                            {zipcodeError && <div className={styles.errorText}>{zipcodeError}</div>}
                            {validatingZipcode && (
                                <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>
                                    <Spinner animation="border" size="sm" className="me-1" /> Validando código postal...
                                </div>
                            )}
                        </Form.Group>
                    </div>

                    <div className={styles.formGrid}>
                        <Form.Group>
                            <Form.Label>Ciudad</Form.Label>
                            <Form.Control
                                type="text"
                                value={locationInfo?.city || ""}
                                readOnly
                                placeholder="Se autocompletará con el código postal"
                                className={styles.updateZipcodeModalInput}
                                style={{ backgroundColor: "#f8fafc", cursor: "not-allowed" }}
                            />
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>Estado</Form.Label>
                            <Form.Control
                                type="text"
                                value={locationInfo?.state || ""}
                                readOnly
                                placeholder="Se autocompletará con el código postal"
                                className={styles.updateZipcodeModalInput}
                                style={{ backgroundColor: "#f8fafc", cursor: "not-allowed" }}
                            />
                        </Form.Group>
                    </div>
                </Form>
            </Modal.Body>

            <Modal.Footer className={styles.modalFooter}>
                <Button
                    variant="secondary"
                    onClick={onHide}
                    className={styles.secondaryBtn}
                    disabled={loading}
                >
                    Cancelar
                </Button>
                <Button
                    variant="primary"
                    type="submit"
                    form="update-zipcode-form"
                    className={modalStyles.closeBtn}
                    disabled={loading}
                >
                    {loading ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Guardando...
                        </>
                    ) : (
                        <>
                            <FontAwesomeIcon icon={faSave} className="me-2" /> Guardar Cambios
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default UpdateZipcodeDispatchZoneModal;
